import { useMemo } from "react";
import { Box, Button, Flex, Grid, Input, Stack, Text } from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { AlertTriangle, RotateCcw, Save } from "lucide-react";
import { AppSelect } from "./app-select";

export type StrategySettingDefinition = {
  key: string;
  label: string;
  type: "number" | "boolean" | "string" | "select";
  defaultValue: unknown;
  min?: number;
  max?: number;
  step?: number;
  options?: Array<{ label: string; value: string | number }>;
  description?: string;
  requiresRestart?: boolean;
  dangerous?: boolean;
};

type FormValues = Record<string, unknown>;

function schemaFor(definitions: StrategySettingDefinition[]) {
  const shape: Record<string, z.ZodTypeAny> = {};
  for (const definition of definitions) {
    if (definition.type === "number") {
      let rule = z.coerce.number();
      if (definition.min !== undefined) rule = rule.min(definition.min, `${definition.label} must be ≥ ${definition.min}`);
      if (definition.max !== undefined) rule = rule.max(definition.max, `${definition.label} must be ≤ ${definition.max}`);
      shape[definition.key] = rule;
    } else if (definition.type === "boolean") {
      shape[definition.key] = z.preprocess((value) => value === true || value === "true", z.boolean());
    } else {
      shape[definition.key] = z.string().min(1, `${definition.label} is required`);
    }
  }
  return z.object(shape);
}

export function DynamicStrategySettingsForm({
  definitions,
  values,
  locked = false,
  onSubmit,
}: {
  definitions: StrategySettingDefinition[];
  values?: FormValues;
  locked?: boolean;
  onSubmit?: (values: FormValues) => void;
}) {
  const defaults = useMemo(() => Object.fromEntries(definitions.map((d) => [d.key, values?.[d.key] ?? d.defaultValue])), [definitions, values]);
  const schema = useMemo(() => schemaFor(definitions), [definitions]);
  const { register, handleSubmit, formState, setError, reset, setValue, watch } = useForm<FormValues>({ defaultValues: defaults });

  const submit = handleSubmit((raw) => {
    const parsed = schema.safeParse(raw);
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        const field = String(issue.path[0] ?? "root");
        setError(field, { type: "validate", message: issue.message });
      }
      return;
    }
    onSubmit?.(parsed.data);
  });

  return (
    <Stack as="form" gap="4" onSubmit={submit}>
      {locked && (
        <Flex gap="2.5" p="3" borderWidth="1px" borderColor="rgba(245,158,11,.28)" bg="rgba(245,158,11,.08)" borderRadius="4px" align="start">
          <Box as={AlertTriangle} boxSize="4" mt="0.5" color="status.warning" />
          <Box>
            <Text fontSize="xs" fontWeight="750" color="status.warning">Settings locked while bot is active</Text>
            <Text mt="0.5" fontSize="10px" color="fg.muted">Pause or stop the bot before changing strategy or risk settings.</Text>
          </Box>
        </Flex>
      )}

      <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap="3">
        {definitions.map((definition) => {
          const error = formState.errors[definition.key]?.message;
          const common = register(definition.key);
          const watched = watch(definition.key);
          return (
            <Box key={definition.key} p="3.5" borderWidth="1px" borderColor={error ? "status.negative" : "border.default"} bg="bg.subtle" borderRadius="4px">
              <Flex align="center" justify="space-between" gap="3" mb="2">
                <Text fontSize="xs" fontWeight="700">{definition.label}</Text>
                <Flex gap="1.5" align="center">
                  {definition.dangerous && <Text fontSize="9px" fontWeight="800" color="status.negative">RISK</Text>}
                  {definition.requiresRestart && <Text fontSize="9px" fontWeight="800" color="status.warning">RESTART</Text>}
                </Flex>
              </Flex>

              {definition.type === "select" ? (
                <AppSelect
                  ariaLabel={definition.label}
                  name={definition.key}
                  value={String(watched ?? "")}
                  disabled={locked}
                  invalid={Boolean(error)}
                  options={(definition.options ?? []).map((option) => ({
                    label: option.label,
                    value: String(option.value),
                  }))}
                  onValueChange={(value) =>
                    setValue(definition.key, value, {
                      shouldDirty: true,
                      shouldTouch: true,
                    })
                  }
                />
              ) : definition.type === "boolean" ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={locked}
                  onClick={() => common.onChange({ target: { name: definition.key, value: watched ? "false" : "true" } })}
                >
                  {watched === true || watched === "true" ? "Enabled" : "Disabled"}
                </Button>
              ) : (
                <Input
                  {...common}
                  disabled={locked}
                  type={definition.type === "number" ? "number" : "text"}
                  min={definition.min}
                  max={definition.max}
                  step={definition.step}
                  bg="bg.panel"
                  borderColor="border.default"
                  fontFamily={definition.type === "number" ? "mono" : "body"}
                />
              )}

              <Flex mt="2" justify="space-between" gap="3">
                <Text fontSize="9px" color={error ? "status.negative" : "fg.subtle"}>{String(error ?? definition.description ?? "")}</Text>
                {definition.type === "number" && <Text flexShrink="0" fontSize="9px" color="fg.subtle">{definition.min ?? "—"} → {definition.max ?? "—"}</Text>}
              </Flex>
            </Box>
          );
        })}
      </Grid>

      <Flex justify="flex-end" gap="2">
        <Button type="button" variant="outline" size="sm" disabled={locked || !formState.isDirty} onClick={() => reset(defaults)}>
          <Box as={RotateCcw} boxSize="3.5" /> Reset
        </Button>
        <Button type="submit" size="sm" bg="brand.500" color="white" disabled={locked || formState.isSubmitting}>
          <Box as={Save} boxSize="3.5" /> Save settings
        </Button>
      </Flex>
    </Stack>
  );
}
